import type { MemberChanges } from '@repo/types/memberChanges';
import type { UserPublic } from '@repo/types/users';

import { useEditGroup } from '@repo/api/group/useEditGroup';
import { useGetGroup } from '@repo/api/group/useGetGroup';
import { useGetGroupUsers } from '@repo/api/group/useGetGroupUsers';
import { useGetUsersInfinite } from '@repo/api/user/useGetUsersInfinite';
import { useDebounceValue } from '@repo/hooks/useDebounceValue';
import { useInfiniteQueryResult } from '@repo/hooks/useInfiniteQueryResult';
import { cn } from '@repo/lib/cn';
import { baseErrorMessages } from '@repo/lib/messages/baseErrorMessages';
import { countStudents, getStudentIdsOverSlots } from '@repo/lib/user-lvl/countStudents';
import { getUserLvl } from '@repo/lib/user-lvl/getUserLvl';
import { CheckCircle2Icon, InfoIcon, UserIcon, UsersIcon } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { InfiniteScroll } from '@/components/ui/infinite-scroll';
import { SearchInput } from '@/components/ui/input';
import { LoadingOverlay, LoadingOverlayLabel, LoadingOverlaySpinner } from '@/components/ui/loading-overlay';
import { LoadingSwap } from '@/components/ui/loading-swap';
import {
  SelectList,
  SelectListElement,
  SelectListElementContent,
  SelectListElementDescription,
  SelectListElementSkeleton,
  SelectListElementTitle,
  SelectListElementTrigger,
  SelectListInfiniteQueryStates,
} from '@/components/ui/select-list';
import { Skeleton } from '@/components/ui/skeleton';
import { Tag } from '@/components/ui/tags';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { toasterMessageHandler } from '@/lib/toaster-message-handler';
import { getAvatar } from '@/utils/getAvatar';

/**
 * Props for the EditGroupUsers component.
 * @property {boolean} open - Whether the dialog is open.
 * @property {(value: boolean) => void} onOpenChange - Callback to change dialog open state.
 * @property {number} [id] - The group ID to edit users for.
 */
interface EditGroupUsersProps {
  open: boolean;
  onOpenChange: (value: boolean) => void;
  id?: number;
}

export default function EditGroupUsers(editGroupUsersProps: EditGroupUsersProps) {
  const { id, open, onOpenChange } = editGroupUsersProps;
  const [searchUserValue, setSearchUserValue] = useState<string>('');
  const searchUserDebouncedValue = useDebounceValue(searchUserValue, 400);
  const [selectedUsers, setSelectedUsers] = useState<UserPublic[]>([]);
  const { data: groupUsers = [], ...groupUsersResults } = useGetGroupUsers(id);
  const { data: groupData, isLoading: isGroupDataLoading } = useGetGroup(id, open);

  const { errorMessage: usersErrorMessage, ...getUsersInfinite } = useGetUsersInfinite(searchUserDebouncedValue);

  const { data: users, isEmpty: usersIsEmpty, noResults: usersNoResults } = useInfiniteQueryResult(getUsersInfinite.data, searchUserDebouncedValue);

  const { mutateAsync: groupEdit, isPending } = useEditGroup();

  const onClose = () => {
    onOpenChange(false);
    setSearchUserValue('');
    setSelectedUsers([]);
  };

  const onRemoveUser = (userId: number) => {
    setSelectedUsers((prev) => prev.filter((user) => user.id !== userId));
  };

  const onSubmit = async () => {
    if (!id) {
      return;
    }

    const selectedUserIds = selectedUsers.map((u) => u.id);
    const usersToAdd = selectedUserIds.filter((userId) => !groupUsers.some((groupUser) => groupUser.id === userId));
    const usersToRemove = groupUsers.filter((user) => !selectedUserIds.includes(user.id)).map((user) => user.id);

    const memberChanges: MemberChanges[] = [];
    if (usersToAdd.length) {
      memberChanges.push({
        userIds: usersToAdd,
        add: true,
      });
    }
    if (usersToRemove.length) {
      memberChanges.push({
        userIds: usersToRemove,
        remove: true,
      });
    }

    await groupEdit({
      id: id,
      memberChanges: memberChanges,
    })
      .then(() => {
        onClose();
      })
      .catch((error) => {
        toasterMessageHandler(baseErrorMessages, error);
      });
  };

  useEffect(() => {
    if (groupUsersResults.isSuccess && open) {
      setSelectedUsers(groupUsers);
    }
  }, [groupUsersResults.isSuccess, open, groupUsers]);

  useEffect(() => {
    if (groupUsersResults.isError) {
      onClose();
      toasterMessageHandler(baseErrorMessages, groupUsersResults.error);
    }
  }, [groupUsersResults.isError, groupUsersResults.error, onOpenChange]);

  const studentsCount = countStudents(selectedUsers);
  // A slots value of 0 means no limit, matching the counter below.
  const studentIdsOverSlots = getStudentIdsOverSlots(selectedUsers, groupData?.slots || null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={'sm:h-[85dvh] sm:min-h-96 sm:max-w-108'} onCloseAutoFocus={onClose} onEscapeKeyDown={onClose}>
        <DialogBody>
          <LoadingOverlay isLoading={isPending}>
            <LoadingOverlaySpinner />
            <LoadingOverlayLabel>Trwa zapisywanie zmian...</LoadingOverlayLabel>
          </LoadingOverlay>
          <LoadingOverlay isLoading={groupUsersResults.isFetching}>
            <LoadingOverlaySpinner />
            <LoadingOverlayLabel>Trwa ładowanie danych...</LoadingOverlayLabel>
          </LoadingOverlay>
          <DialogHeader className={'border-border-primary bg-card items-center gap-2 border-b'}>
            <FeaturedIcon size={'lg'}>
              <UsersIcon />
            </FeaturedIcon>
            <div className={'flex min-w-0 grow flex-col gap-0.5'}>
              <DialogTitle>
                Edytuj użytkowników grupy
                {groupData?.name ? ` ${groupData.name}` : '...'}
              </DialogTitle>
              <DialogDescription className={'text-muted-foreground text-xs'}>Wybierz użytkowników grupy.</DialogDescription>
            </div>
          </DialogHeader>
          <div className={'flex grow flex-col gap-3 overflow-y-auto p-4'}>
            <div>
              <div className={'flex grow flex-col gap-4'}>
                <SearchInput setValue={setSearchUserValue} />
                <div className={'flex justify-between'}>
                  <h5 className={'font-medium'}>Użytkownicy:</h5>
                  {isGroupDataLoading ? (
                    <Skeleton className="my-auto h-4 w-24 rounded-xs" />
                  ) : (
                    <div className="flex items-center gap-2">
                      <Tooltip>
                        <TooltipTrigger asChild className={cn(!(groupData && groupData.slots && studentsCount > groupData.slots) && 'hidden', 'cursor-pointer')}>
                          <InfoIcon className="text-text-warning size-4" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-50">
                          Zaznaczeni użytkownicy grupy podkreśleni poniżej przekraczają zalecany limit miejsc. Liczba miejsc jest opcjonalna i ma charakter informacyjny.
                        </TooltipContent>
                      </Tooltip>
                      <p
                        className={cn(
                          'text-muted-foreground my-auto min-w-36 text-right text-xs font-medium whitespace-nowrap tabular-nums',
                          groupData?.slots && studentsCount > groupData?.slots && 'text-text-warning',
                          groupData?.slots === undefined && 'hidden'
                        )}
                      >
                        Liczba miejsc: {studentsCount} / {groupData?.slots}
                      </p>
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-1 empty:hidden">
                  {selectedUsers.map(({ id, name, surname, avatarURL }) => (
                    <Tag key={id} onRemove={() => onRemoveUser(id)} variant={studentIdsOverSlots.has(id) ? 'warning' : 'muted'} border>
                      <Avatar size={'xs'}>
                        <AvatarImage src={avatarURL ? getAvatar(avatarURL) : undefined} alt={name + '_' + surname} />
                        <AvatarFallback>
                          <UserIcon />
                        </AvatarFallback>
                      </Avatar>
                      {name} {surname.at(0)}.
                    </Tag>
                  ))}
                </div>
                <SelectList
                  type={'multiple'}
                  selected={selectedUsers.map((u) => String(u.id))}
                  setSelected={(ids) => {
                    const selectedIds = new Set(ids.map(Number));
                    setSelectedUsers((prev) => {
                      const existing = prev.filter((u) => selectedIds.has(u.id));
                      const newUsers = users.filter((u) => selectedIds.has(u.id) && !existing.some((e) => e.id === u.id));
                      return [...existing, ...newUsers];
                    });
                  }}
                >
                  <SelectListInfiniteQueryStates
                    query={getUsersInfinite}
                    isEmpty={usersIsEmpty}
                    noResults={usersNoResults}
                    isEmptyMessage={'Wyszukaj użytkownika'}
                    noResultMessage={'Nie znaleziono użytkownika'}
                    errorMessage={usersErrorMessage}
                  />

                  {users.map((user) => (
                    <SelectListElement key={user.id} id={String(user.id)}>
                      <Avatar size={'sm'}>
                        <AvatarImage src={getAvatar(user.avatarURL)} alt={user.name} />
                        <AvatarFallback>{user.name[0] ?? <UsersIcon />}</AvatarFallback>
                      </Avatar>
                      <SelectListElementContent>
                        <SelectListElementTitle>
                          {user.name} {user.surname}
                        </SelectListElementTitle>
                        <SelectListElementDescription>{getUserLvl(user.lvl)}</SelectListElementDescription>
                      </SelectListElementContent>

                      <SelectListElementTrigger>
                        {({ selected, toggleElement }) => (
                          <Checkbox
                            checked={selected}
                            onCheckedChange={() => {
                              toggleElement();
                            }}
                            className="after:absolute after:inset-0"
                          />
                        )}
                      </SelectListElementTrigger>
                    </SelectListElement>
                  ))}
                  <InfiniteScroll
                    hasNextPage={getUsersInfinite.hasNextPage}
                    isLoading={getUsersInfinite.isLoading}
                    isFetching={getUsersInfinite.isFetching}
                    next={getUsersInfinite.fetchNextPage}
                    threshold={0.65}
                  >
                    {getUsersInfinite.hasNextPage && <SelectListElementSkeleton />}
                  </InfiniteScroll>
                </SelectList>
              </div>
            </div>
          </div>
        </DialogBody>
        <DialogFooter className={'border-border-primary dark:bg-card bg-accent border-t'}>
          <Button onClick={onClose} type={'button'} variant={'tertiary'} className={'w-full md:w-fit'}>
            Anuluj
          </Button>
          <Button disabled={isPending} onClick={onSubmit} type={'submit'} variant={'primary'} className={'w-full md:w-fit'}>
            <LoadingSwap isLoading={isPending} className="flex items-center gap-2">
              <CheckCircle2Icon />
              Zapisz
            </LoadingSwap>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
